import React, { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { IconCheck, IconCopy, IconLink } from "@tabler/icons-react";
import { toast } from "react-hot-toast";

import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { programService } from "../../services/program.service";
import { Program } from "../../types/program";
import { buildJoinPortalUrl } from "../../utils/programShareLink";
import { cn } from "@/lib/utils";

interface JoinPortalLinkPopoverProps {
  /** Brand username — the `/store/<username>/join` part of every link. */
  username: string;
}

/**
 * "Join Portal Link" — one link per program.
 *
 * A brand can run several programs, but affiliates only ever land on the
 * default one unless the brand assigns them by hand. Each row here copies a
 * link carrying that program's `shareToken`, so whoever opens it joins that
 * program directly. See docs/PROGRAM_SHARE_LINKS.md.
 */
export const JoinPortalLinkPopover: React.FC<JoinPortalLinkPopoverProps> = ({
  username,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Shares the "my-programs" cache key so a share-link rotation elsewhere
  // invalidates this list too — a popover holding its own copy would happily
  // hand out a link that no longer resolves. `enabled` keeps it off the wire
  // until someone actually opens it; most page views never do.
  const {
    data: programs,
    isLoading,
    isError,
  } = useQuery<Program[]>({
    queryKey: ["my-programs"],
    queryFn: () => programService.getMyPrograms(),
    enabled: isOpen,
  });

  const copyLink = useCallback(
    async (program: Program) => {
      const url = buildJoinPortalUrl(username, program.shareToken);
      try {
        await navigator.clipboard.writeText(url);
        setCopiedId(program.id);
        toast.success("Link copied");
        setTimeout(
          () => setCopiedId((current) => (current === program.id ? null : current)),
          2000
        );
      } catch {
        toast.error("Could not copy the link");
      }
    },
    [username]
  );

  // Same vocabulary the programs table uses, so a brand sees one name per
  // program wherever they look.
  const programLabel = (program: Program) => {
    if (program.isDefault && (!program.name || program.name === "Default Program")) {
      return "Discoverable Program";
    }
    return program.name || "Custom Program";
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="min-h-[40px] min-w-fit flex-shrink-0">
          <IconLink size={16} stroke={1.75} />
          <span className="hidden sm:inline">Join Portal Link</span>
          <span className="sm:hidden">Join Link</span>
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[22rem] overflow-hidden p-0">
        <div className="max-h-80 overflow-y-auto">
          {isLoading && (
            <div className="space-y-3 p-4">
              {[1, 2].map((i) => (
                <div key={i} className="animate-pulse space-y-2">
                  <div className="h-3.5 w-1/2 rounded bg-muted" />
                  <div className="h-3 w-1/3 rounded bg-muted" />
                </div>
              ))}
            </div>
          )}

          {!isLoading && isError && (
            <p className="p-4 text-[13px] text-muted-foreground">
              Programs could not be loaded. Close this and try again.
            </p>
          )}

          {!isLoading && !isError && programs?.length === 0 && (
            <p className="p-4 text-[13px] text-muted-foreground">No programs yet.</p>
          )}

          {!isLoading &&
            programs?.map((program) => {
              const isCopied = copiedId === program.id;
              const isSuspended = program.status === "SUSPENDED";

              return (
                <button
                  key={program.id}
                  type="button"
                  onClick={() => copyLink(program)}
                  className="flex w-full items-center justify-between gap-3 border-t border-border px-4 py-2.5 text-left transition-colors first:border-t-0 hover:bg-accent focus-visible:outline-none focus-visible:bg-accent"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-[13.5px] font-medium text-foreground">
                      {programLabel(program)}
                    </span>
                    <span className="mt-0.5 flex items-center gap-x-3 text-xs text-muted-foreground">
                      <span>
                        {program.commissionValue}
                        {program.commissionType === "PERCENTAGE" ? "%" : " USD"} commission
                      </span>
                      <span>{program.attributionMethod === "SNEAKYLINK" ? "Link" : "Code"}</span>
                      {isSuspended && (
                        <span className="inline-flex items-center gap-1.5">
                          <span aria-hidden className="size-1.5 rounded-full bg-amber-500" />
                          Suspended
                        </span>
                      )}
                    </span>
                  </span>

                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1 text-xs font-medium",
                      isCopied ? "text-emerald-500" : "text-muted-foreground"
                    )}
                  >
                    {isCopied ? (
                      <>
                        <IconCheck size={14} stroke={1.75} /> Copied
                      </>
                    ) : (
                      <>
                        <IconCopy size={14} stroke={1.75} /> Copy
                      </>
                    )}
                  </span>
                </button>
              );
            })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default JoinPortalLinkPopover;
